import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Loader2Icon } from 'lucide-react'
import { useMemo } from 'react'

import { useGetCompany } from '@/api/company.hook'
import { useGetUserById } from '@/api/get-user-by-id.hook'

import { formatDate, timeSince } from '@/lib/date/date'
import { useIsProjectionDone } from '@/lib/risk/is-projection-done.hook'
import { useLastIrqChange } from '@/lib/risk/last-irq-change.hook'
import { cn } from '@/lib/ui/style-helpers'

import { UserScoreDisplay } from '@/components/shared/user-score-display/user-score-display'

import { USER_SCORE_INFO_LABELS } from './labels'

type UserScoreInfoProps = {
  companyId: string
  variant?: 'default' | 'compact'
  className?: string
}

export const UserScoreInfo = ({
  companyId,
  variant = 'default',
  className,
}: UserScoreInfoProps) => {
  const { data } = useGetCompany(companyId)
  const isProjectionDone = useIsProjectionDone(
    data?.company?.validatedRisk,
    data?.company?.calculatedRisk,
  )
  const { lastIRQChange, isLoading: isLastIrqChangeLoading } = useLastIrqChange(companyId)

  const integrationSource = lastIRQChange.integrationSource
  const userResult = useGetUserById(lastIRQChange.userId)
  const isLoadingUser = !integrationSource && 'isLoading' in userResult && userResult.isLoading
  const user = userResult.user
  const isUserAvailable =
    !integrationSource && !('isError' in userResult && userResult.isError) && !!user

  const timeSinceLastScorer = useMemo(() => {
    if (!lastIRQChange.time) return null
    const { value, unit } = timeSince(
      lastIRQChange.time ? timestampDate(lastIRQChange.time) : new Date(),
    )
    return `${value} ${unit} ago`
  }, [lastIRQChange.time])

  if (isLastIrqChangeLoading || isLoadingUser) {
    return <Loader2Icon className='size-4 animate-spin' />
  }

  if (!isProjectionDone) {
    return (
      <div
        className={cn('flex items-center gap-1.5 text-xs text-main-foreground-tertiary', className)}
      >
        <Loader2Icon className='size-4 animate-spin duration-2000' />
        <span>{USER_SCORE_INFO_LABELS.ESTIMATING_INHERENT_RISK}</span>
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <div className={cn('flex flex-col gap-0.5 truncate text-nowrap text-body-small', className)}>
        {integrationSource ? (
          <span className='text-main-foreground-tertiary'>
            {USER_SCORE_INFO_LABELS.SCORED_VIA_INTEGRATION}
          </span>
        ) : (
          isUserAvailable && (
            <UserScoreDisplay userId={lastIRQChange.userId} userName={user?.name} />
          )
        )}
        <span className='text-main-foreground-tertiary'>{timeSinceLastScorer}</span>
      </div>
    )
  }

  const hasScorer = integrationSource || isUserAvailable

  return (
    <span className={cn('flex items-center gap-1 text-nowrap text-body-small', className)}>
      {integrationSource ? (
        <span className='text-nowrap text-main-foreground-tertiary'>
          {USER_SCORE_INFO_LABELS.SCORED_VIA_INTEGRATION}
        </span>
      ) : (
        isUserAvailable && (
          <>
            <span className='text-nowrap text-main-foreground-primary'>
              {USER_SCORE_INFO_LABELS.SCORED_BY}
            </span>
            <UserScoreDisplay userId={lastIRQChange.userId} userName={user?.name || user?.email} />
          </>
        )
      )}
      <span className='text-nowrap text-main-foreground-tertiary'>
        {hasScorer ? USER_SCORE_INFO_LABELS.ON_PREFIX : USER_SCORE_INFO_LABELS.SCORED_ON_PREFIX}
        {lastIRQChange.time &&
          formatDate(
            lastIRQChange.time ? timestampDate(lastIRQChange.time) : new Date(),
            'shortDateTime',
          )}{' '}
        ({timeSinceLastScorer})
      </span>
    </span>
  )
}
